/**
 * Theme store — VU-MODERN ↔ VU-BRUTALIST as parity equals.
 * Now also supports light/dark mode for VU-MODERN.
 *
 * Persisted in localStorage: `vu:theme` and `vu:mode`.
 * Applied to <html data-theme data-mode> via $effect in the layout.
 */

export type ThemeName = 'modern' | 'brutalist';
export type ColorMode = 'dark' | 'light' | 'auto';

const THEME_KEY = 'vu:theme';
const MODE_KEY = 'vu:mode';

class ThemeStore {
  current = $state<ThemeName>('modern');
  mode = $state<ColorMode>('dark');

  /** Resolved mode (auto → actual) */
  resolvedMode = $derived.by(() => {
    if (this.mode !== 'auto') return this.mode;
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  constructor() {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'modern' || saved === 'brutalist') {
        this.current = saved;
      }
      const savedMode = localStorage.getItem(MODE_KEY);
      if (savedMode === 'dark' || savedMode === 'light' || savedMode === 'auto') {
        this.mode = savedMode;
      }
    } catch {
      /* storage disabled */
    }
  }

  toggle(): void {
    this.set(this.current === 'modern' ? 'brutalist' : 'modern');
  }

  set(value: ThemeName): void {
    this.current = value;
    this._persist();
  }

  setMode(mode: ColorMode): void {
    this.mode = mode;
    this._persist();
  }

  cycleMode(): void {
    const modes: ColorMode[] = ['dark', 'light', 'auto'];
    const idx = modes.indexOf(this.mode);
    this.setMode(modes[(idx + 1) % modes.length]!);
  }

  private _persist(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(THEME_KEY, this.current);
      localStorage.setItem(MODE_KEY, this.mode);
    } catch {
      /* storage disabled */
    }
  }
}

export const theme = new ThemeStore();
